import { Icon } from '@iconify-icon/react';
import Image from 'next/image';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import SubscribeForm from '@/components/modules/Webinar/SubscribeForm';
import VideoPlayer from '@/components/modules/Webinar/VideoPlayer';
import { SERIES_LENGTH, webinars } from '@/components/modules/Webinar/webinars';
import PageLink from '@/utils/PageLink';

const event = webinars.find((w) => w.slug === 'using-ai-safely');

// The traffic-light rule is this session's signature idea, so it gets its own
// block rather than sitting in the generic takeaways grid below.
const trafficLight = [
  {
    title: 'RED',
    subtitle: 'Stop before you share',
    text: 'Client data, financials, passwords, contracts — anything with a name attached. Never in a consumer tool.',
    color: '#d34335',
    bg: '#fff3f0',
  },
  {
    title: 'AMBER',
    subtitle: 'Pause and check',
    text: 'Internal documents, pricing, vendor lists. Get the owner’s OK before it goes in.',
    color: '#9c6809',
    bg: '#fffaeb',
  },
  {
    title: 'GREEN',
    subtitle: 'Go with confidence',
    text: 'Public information, published content, general how-to questions.',
    color: '#19724b',
    bg: '#eef9f3',
  },
];

export default function WebinarReplayPage() {
  if (!event?.replay) return null;

  const fullTitle = `${event.title} ${event.titleAccent ?? ''}`.trim();

  return (
    <div className="bg-white">
      <NextSeo
        title={`${fullTitle} | Webinar Recording`}
        description={`Watch the full 30-minute recording free. The traffic-light rule for what you share with AI, and four rules that keep client data out of the wrong tool. Recorded ${event.date}.`}
        canonical={`https://chat.co/webinars/${event.slug}`}
        openGraph={{
          url: `https://chat.co/webinars/${event.slug}`,
          type: 'video.other',
          title: `${fullTitle} — full recording`,
          description:
            'Free recording · Session 1 of 6 · A practical 30-minute workshop for small businesses.',
        }}
      />
      <Header />

      <main>
        {/* ── Hero ── */}
        <section className="relative overflow-hidden bg-[#06264b] text-white">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-32 -top-64 h-[650px] w-[650px] rounded-full bg-[radial-gradient(circle_at_top,#c6a75055,transparent_70%)]"
          />
          <div className="relative mx-auto w-full max-w-7xl px-6 pb-14 pt-8 lg:px-8 lg:pb-16">
            <Link
              href={PageLink.webinars}
              className="inline-flex items-center gap-1.5 text-sm text-blue-200 transition hover:text-white">
              <Icon icon="tabler:arrow-left" className="text-base" />
              All webinars
            </Link>
            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Image
                src="/images/sa-cyber-logo.png"
                alt="SA Cyber"
                width={1005}
                height={226}
                className="h-auto w-32 mix-blend-screen contrast-200 invert sm:w-36"
                priority
              />
              <span className="text-sm text-blue-200 sm:border-l sm:border-white/20 sm:pl-5">
                Creators of Chat.co
              </span>
            </div>
            <p className="mt-9 text-xs font-bold uppercase tracking-[0.22em] text-[#e6ba50]">
              Free recording <span className="px-2">·</span> Session {event.session} of{' '}
              {SERIES_LENGTH}
            </p>
            <h1 className="mt-5 max-w-4xl text-4xl font-bold leading-[1.08] tracking-tight sm:text-6xl lg:text-7xl">
              {event.title}
              <br />
              <span className="text-[#e6ba50]">{event.titleAccent}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-blue-100 sm:text-xl">
              A practical 30-minute workshop for small businesses.
              <br className="hidden sm:block" /> More confidence. Fewer risks. One simple rule.
            </p>
            <a
              href="#recording"
              className="mt-7 inline-flex items-center gap-3 rounded-lg bg-[#e6ba50] px-6 py-3 text-sm font-bold text-[#06264b] transition hover:bg-[#f3ce76] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
              <Icon icon="tabler:player-play-filled" className="text-base" />
              Watch the recording
            </a>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/15 pt-6 text-sm">
              <span>
                ▦ Recorded <strong className="ml-1">{event.date}</strong>
              </span>
              <span>
                ◷ <strong className="ml-1">{event.duration}</strong>
              </span>
              <span className="text-blue-200">Free to watch · No sign-up required</span>
            </div>
          </div>
        </section>

        {/* ── The recording ── */}
        <section id="recording" className="scroll-mt-20 bg-white py-12 sm:py-16">
          <div className="mx-auto w-full max-w-5xl px-6 lg:px-8">
            <VideoPlayer
              src={event.replay}
              startAt={event.replayStartsAt}
              eyebrow={`Session ${event.session} of ${SERIES_LENGTH} · Full recording`}
              title={fullTitle}
              caption={`${event.date} · ${event.duration}`}
            />
            <p className="mt-4 text-sm text-slate-500">
              Missed it live? This is the whole session — nothing held back. It opens where we start
              speaking; rewind if you want the few minutes before that.
            </p>
          </div>
        </section>

        {/* ── The traffic-light rule ── */}
        <section className="bg-[#f5f7fa] py-12 sm:py-16">
          <div className="mx-auto grid w-full max-w-7xl items-start gap-10 px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#866112]">
                Practical guidance. Plain English.
              </p>
              <h2 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-[#06264b] sm:text-4xl">
                Before you paste it into AI,
                <br className="hidden sm:block" /> know where it belongs.
              </h2>
              <p className="mt-5 text-base leading-7 text-slate-600">
                AI can help your business move faster. Knowing what to share — and what to keep
                private — is the first step to using it with confidence.
              </p>
              <p className="mt-4 text-base leading-7 text-slate-600">
                The session opens with a simple traffic-light rule for what is safe to put into AI,
                what needs a second look, and what never belongs there.
              </p>
              <div className="mt-8 border-l-2 border-[#d3a63e] pl-5">
                <h3 className="font-semibold text-[#06264b]">Built for your working day</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  For business owners, managers and teams who want practical AI guidance. No
                  technical background needed.
                </p>
              </div>
            </div>
            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white">
              {trafficLight.map((item) => (
                <div
                  key={item.title}
                  className="flex gap-4 border-b border-slate-100 p-6 last:border-0">
                  <div
                    style={{ background: item.bg, color: item.color }}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full">
                    <span style={{ background: item.color }} className="h-4 w-4 rounded-full" />
                  </div>
                  <div>
                    <p style={{ color: item.color }} className="text-xs font-bold tracking-widest">
                      {item.title}
                    </p>
                    <h3 className="mt-1 font-semibold text-[#06264b]">{item.subtitle}</h3>
                    <p className="mt-1.5 text-sm leading-6 text-slate-600">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── The four rules ── */}
        {event.takeaways && (
          <section className="bg-white py-12 sm:py-16">
            <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                The four rules we walked through
              </h2>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-gray-600">
                What goes wrong, and what to look for instead — whatever AI tool your team uses.
              </p>
              <div className="mt-9 grid gap-5 md:grid-cols-2">
                {event.takeaways.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-[28px] border border-gray-100 bg-[#f8fafc] p-6 sm:p-7">
                    <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#866112]">
                      {item.label}
                    </p>
                    <h3 className="mt-3 text-lg font-semibold leading-snug text-gray-900">
                      {item.title}
                    </h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-gray-600">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Next session + subscribe ── */}
        <section className="bg-[#f6f8fb] py-12 sm:py-16">
          <div className="mx-auto grid w-full max-w-7xl items-start gap-10 px-6 lg:grid-cols-[1fr_minmax(0,440px)] lg:gap-14 lg:px-8">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                This was session {event.session} of {SERIES_LENGTH}.
              </h2>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-gray-600">
                Five more sessions follow, same time next month, same 30 minutes. Leave your email
                and we’ll send the invitation as each one is scheduled.
              </p>
              <Link
                href={PageLink.webinars}
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-500">
                See the full series
                <Icon icon="tabler:arrow-right" className="text-base" />
              </Link>
              <p className="mt-8 text-sm text-slate-500">
                Have a question about the session?{' '}
                <a
                  href="mailto:hello@chat.co"
                  className="font-medium text-[#15558e] underline underline-offset-4">
                  hello@chat.co
                </a>
              </p>
            </div>
            <SubscribeForm />
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="bg-white py-12 sm:py-16">
          <div className="mx-auto w-full max-w-4xl px-6 lg:px-8">
            <div className="rounded-[28px] bg-primary-600 px-8 py-10 text-center sm:px-12">
              <h2 className="text-2xl font-bold text-white sm:text-3xl">
                Want this walked through on your own documents?
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-base text-blue-100">
                We can show your team the four safeguards inside your own workspace, in about twenty
                minutes. Bring one question from your business.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link
                  href={PageLink.demo}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-primary-600 hover:bg-blue-50 sm:w-auto">
                  Book a 20-minute walkthrough
                  <Icon icon="tabler:arrow-right" className="text-lg" />
                </Link>
                <Link
                  href={PageLink.privacyPage}
                  className="inline-flex w-full items-center justify-center rounded-full border border-white/40 px-6 py-3 text-sm font-medium text-white hover:bg-white/10 sm:w-auto">
                  How Chat.co protects your data
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
